import { useState, useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ScanField } from "@/components/ScanField";
import { PhotoUploader } from "@/components/PhotoUploader";
import { PhotoFixAction } from "@/components/PhotoFixAction";
import { PhotoThumbs } from "@/components/PhotoThumbs";
import { LiveCameraCapture } from "@/components/LiveCameraCapture";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ScanLine, CheckCircle2, SkipForward, Home, Lock, Package, Camera, X, Loader2, AlertTriangle, Check, Copy, RotateCw } from "lucide-react";
import { useFloorReturn } from "@/lib/floorReturn";
import { scanAccept, scanRefuse, scanSoundOnly } from "@/lib/scanFeedback";
import { haptic } from "@/lib/floor";

const partLine = (p) => [p?.year, p?.make, p?.model].filter(Boolean).join(" ");
let _sid = 0;
const sid = () => `s${++_sid}_${Date.now()}`;

export default function RapidCapturePage() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [stage, setStage] = useState("scan");      // scan | photos | done
  const [busy, setBusy] = useState(false);
  const [part, setPart] = useState(null);
  const [camOpen, setCamOpen] = useState(false);
  const [armNextScan, setArmNextScan] = useState(false);  // auto-open scan camera after a Next Part
  const [captureSession, setCaptureSession] = useState("");
  // shots: {id, url, status: uploading|done|skipped|error, file_id?, msg?, file, ctrl}
  const [shots, setShots] = useState([]);
  const [session, setSession] = useState({ parts: 0, photos: 0 });
  const [guard, setGuard] = useState(null);         // pending leave intent: {type:'next'|'complete'|'href', href?}

  const shotsRef = useRef(shots);
  shotsRef.current = shots;

  const savedCount = shots.filter((s) => s.status === "done" || s.status === "skipped").length;
  const uploadingCount = shots.filter((s) => s.status === "uploading").length;
  const failedCount = shots.filter((s) => s.status === "error").length;
  const hasPending = uploadingCount > 0 || failedCount > 0;   // the ONLY thing the leave-guard cares about
  const hasPendingRef = useRef(hasPending);
  hasPendingRef.current = hasPending;

  // Mirror unsaved-work state to the Floor round-trip bar (prompts before Back to Floor).
  const fr = useFloorReturn();
  useEffect(() => { fr?.setDirty(hasPending); return () => fr?.setDirty(false); }, [hasPending, fr]);

  const patch = (id, next) => setShots((prev) => prev.map((s) => (s.id === id ? { ...s, ...next } : s)));

  // ---- SCAN STAGE ----
  const onScan = useCallback(async (code) => {
    if (!code || busy) return;
    setBusy(true);
    try {
      const { data } = await api.get(`/parts/resolve-scan`, { params: { code } });
      setPart(data); setShots([]); setCaptureSession(sid()); setCamOpen(true); setStage("photos");
      scanAccept(data.short_code || data.tag_code || "PART");
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail);
      scanRefuse(msg || "TAG NOT FOUND — TRY AGAIN");
      toast.error(msg || "Tag not found — try again");
    } finally { setBusy(false); }
  }, [busy]);

  // ---- UPLOAD-AS-YOU-SHOOT: each shot uploads immediately in the background ----
  const uploadShot = useCallback(async (shot) => {
    const ctrl = new AbortController();
    patch(shot.id, { status: "uploading", msg: "", ctrl });
    const fd = new FormData();
    fd.append("file", shot.file);
    if (shot.session_id) fd.append("session_id", shot.session_id);
    try {
      const { data } = await api.post(`/parts/${shot.partId}/photos`, fd, { timeout: 120000, signal: ctrl.signal });
      if (data?.duplicate) patch(shot.id, { status: "skipped", file_id: data.photo?.file_id, msg: "already there" });
      else { patch(shot.id, { status: "done", file_id: data.photo?.file_id, msg: "" }); }
      scanSoundOnly(true);   // a photo landing is not a scan — the cue, without a tint over the burst
    } catch (e) {
      if (e?.code === "ERR_CANCELED") return;   // deleted mid-flight — its tile is already gone
      patch(shot.id, { status: "error", msg: formatApiErrorDetail(e.response?.data?.detail) || "Upload failed — tap retry" });
      scanSoundOnly(false);
    }
  }, []);

  const addShot = useCallback((file) => {
    const shot = { id: sid(), url: URL.createObjectURL(file), status: "uploading", file,
                   partId: part.id, session_id: captureSession };
    setShots((prev) => [shot, ...prev]);
    uploadShot(shot);
  }, [part, captureSession, uploadShot]);

  const retryShot = (shot) => uploadShot(shot);

  const deleteShot = async (shot) => {
    // in-flight → cancel the upload; nothing persisted
    if (shot.status === "uploading" && shot.ctrl) { try { shot.ctrl.abort(); } catch { /* ignore */ } }
    // already saved → narrow self-remove (own photo, own session) — soft-archive on the server
    if ((shot.status === "done" || shot.status === "skipped") && shot.file_id) {
      try {
        await api.post(`/parts/${shot.partId}/photos/${shot.file_id}/remove-mine`, { session_id: shot.session_id });
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't remove that photo.");
        return;   // keep the tile so nothing silently mismatches the server
      }
    }
    URL.revokeObjectURL(shot.url);
    setShots((prev) => prev.filter((s) => s.id !== shot.id));
    haptic(10);
  };

  const cleanupUrls = () => shotsRef.current.forEach((s) => URL.revokeObjectURL(s.url));

  const doStartNextPart = () => {
    toast.success(`${part.short_code}: ${savedCount} photo${savedCount === 1 ? "" : "s"} ✓`);
    setSession((s) => ({ parts: s.parts + (savedCount ? 1 : 0), photos: s.photos + savedCount }));
    cleanupUrls();
    setPart(null); setShots([]); setCamOpen(false); setCaptureSession("");
    setArmNextScan(true);   // keep it armed — reopen the scan camera automatically
    setStage("scan");
    haptic(20);
  };
  const doComplete = () => {
    setSession((s) => ({ parts: s.parts + (savedCount ? 1 : 0), photos: s.photos + savedCount }));
    cleanupUrls();
    setShots([]); setCamOpen(false);
    setStage("done");
    scanAccept(`${savedCount} PHOTO${savedCount === 1 ? "" : "S"} SAVED`);
  };

  // ---- NAVIGATION GUARD (only when something is genuinely still uploading or failed) ----
  const executeLeave = (intent) => {
    setGuard(null);
    if (!intent) return;
    if (intent.type === "next") doStartNextPart();
    else if (intent.type === "complete") doComplete();
    else if (intent.type === "href") { cleanupUrls(); nav(intent.href); }
  };
  const requestLeave = (intent) => { if (hasPending) setGuard(intent); else executeLeave(intent); };
  // If the last uploads finish while the guard is open (nothing failed), auto-continue.
  useEffect(() => {
    if (guard && !hasPending) executeLeave(guard);
    // eslint-disable-next-line
  }, [hasPending, guard]);
  const guardDiscardFailed = () => {
    setShots((prev) => {
      prev.filter((s) => s.status === "error").forEach((s) => URL.revokeObjectURL(s.url));
      return prev.filter((s) => s.status !== "error");
    });
    // executeLeave runs via the effect once hasPending clears (no failed, no uploading)
  };

  // Browser refresh / tab close — only block while an upload is genuinely in-flight/failed
  useEffect(() => {
    const onBeforeUnload = (e) => { if (hasPendingRef.current) { e.preventDefault(); e.returnValue = ""; return ""; } };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, []);

  // Browser BACK button
  useEffect(() => {
    window.history.pushState(null, "", window.location.href);
    const onPop = () => {
      if (hasPendingRef.current) {
        window.history.pushState(null, "", window.location.href);
        setGuard({ type: "href", href: "/home" });
      } else {
        window.history.back();
      }
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  // In-app nav links — intercept ONLY while an upload is genuinely pending/failed
  useEffect(() => {
    const onClick = (e) => {
      if (!hasPendingRef.current) return;
      const a = e.target.closest?.("a[href]");
      if (!a) return;
      const href = a.getAttribute("href");
      if (!href || href.startsWith("http") || href.startsWith("#") || href.startsWith("mailto")) return;
      if (href === "/rapid-capture") return;
      e.preventDefault(); e.stopPropagation();
      setGuard({ type: "href", href });
    };
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, []);

  useEffect(() => () => cleanupUrls(), []);   // clean object URLs on unmount

  return (
    <div data-testid="rapid-capture-page" className="max-w-2xl">
      <div className="flex items-center justify-between gap-2 mb-1">
        <h1 className="font-display text-2xl font-bold tracking-tight flex items-center gap-2">
          <ScanLine className="w-6 h-6 text-[#B01E28]" />Rapid Photo Capture
        </h1>
        {stage !== "done" && (session.parts > 0 || stage === "photos") && (
          <Badge data-testid="rapid-session-badge" className="rounded-full border-0 bg-slate-800 text-white text-[11px] font-mono">
            {session.parts + (stage === "photos" && savedCount ? 1 : 0)} parts · {session.photos + savedCount} photos
          </Badge>
        )}
      </div>
      <p className="text-sm text-slate-500 mb-4">Scan a part tag, then shoot — each photo <span className="font-semibold">uploads on its own</span> as you go. When they're all green, tap <span className="font-semibold">Next Part</span> or <span className="font-semibold">Complete</span>.</p>

      {/* ---------------- SCAN STAGE ---------------- */}
      {stage === "scan" && (
        <div data-testid="rapid-scan-stage" className="rounded-2xl border border-slate-200 bg-white p-4">
          <div className="flex items-center gap-2 mb-3 text-slate-700 font-semibold">
            <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-[#B01E28] text-white"><ScanLine className="w-4 h-4" /></span>
            Scan the part tag
          </div>
          <ScanField testid="rapid-scan" onScan={onScan} busy={busy} autoStartCamera={armNextScan}
            label="" placeholder="Point the camera at the QR / barcode, use a scan gun, or type FAS-######…" submitLabel="Look up" />
          <div data-testid="rapid-signed-in-as" className="mt-3 flex items-center gap-1.5 text-[11px] text-slate-400">
            <Check className="w-3.5 h-3.5 text-emerald-500" />
            Auto-signed as <span className="font-semibold text-slate-600">{user?.name || user?.email}</span> — no name to type.
          </div>
        </div>
      )}

      {/* ---------------- PHOTO STAGE (scanning LOCKED) ---------------- */}
      {stage === "photos" && part && (
        <div data-testid="rapid-photo-stage" className="space-y-4">
          <div className="rounded-2xl border border-[#B01E28]/30 bg-[#FFF9F3] p-4">
            <div className="flex items-center justify-between gap-2">
              <div>
                <div data-testid="rapid-part-code" className="font-mono text-2xl font-bold text-slate-900">{part.short_code}</div>
                {partLine(part) && <div className="text-sm text-slate-600">{partLine(part)}</div>}
                {part.part_type && <div className="text-xs text-slate-400 flex items-center gap-1"><Package className="w-3 h-3" />{part.part_type}</div>}
              </div>
              <div className="text-right">
                <Badge data-testid="rapid-saved-count" className="rounded-full border-0 bg-emerald-600 text-white text-xs font-mono px-2.5 py-1">
                  <Check className="w-3 h-3 mr-1" />{savedCount} saved
                </Badge>
                {uploadingCount > 0 && <div data-testid="rapid-pending-count" className="text-[11px] font-mono text-[#C76714] mt-1">{uploadingCount} uploading…</div>}
                {failedCount > 0 && <div data-testid="rapid-failed-count" className="text-[11px] font-mono text-[#B01E28] mt-1">{failedCount} failed</div>}
              </div>
            </div>
            <div className="flex items-center gap-1.5 mt-2 text-[11px] text-slate-500">
              <Lock className="w-3.5 h-3.5 text-[#C76714]" /> Scanning is locked while you shoot — the camera can't jump to another tag.
            </div>
          </div>

          {/* EXISTING photos already on this part — see them so you don't re-shoot a part you already did */}
          {part.existing_photos?.length > 0 && (
            <div data-testid="rapid-existing-photos" className="rounded-2xl border border-amber-300 bg-amber-50 p-4">
              <div data-testid="rapid-existing-warning" className="flex items-center gap-1.5 text-sm font-semibold text-[#B45309] mb-2">
                <AlertTriangle className="w-4 h-4 shrink-0" />
                This part already has {part.existing_photos.length} photo{part.existing_photos.length === 1 ? "" : "s"} — only add more if needed.
              </div>
              <PhotoThumbs photos={part.existing_photos} testidPrefix="rapid-existing" label="existing photo"
                fixScope="part" fixRecordId={part.id} onFixed={() => onScan(part.tag_code || part.short_code)} />
            </div>
          )}

          {/* LIVE BURST camera (primary) */}
          {camOpen ? (
            <LiveCameraCapture testidPrefix="rapid-burst" onCapture={addShot} onClose={() => setCamOpen(false)} />
          ) : (
            <div className="flex flex-wrap items-center gap-2">
              <Button data-testid="rapid-open-camera" onClick={() => setCamOpen(true)}
                className="rounded-xl h-12 px-5 bg-[#B01E28] hover:bg-[#8f1820] text-white text-base font-semibold">
                <Camera className="w-5 h-5 mr-2" />Photograph (live)
              </Button>
              <span className="text-xs text-slate-400">or use the fallback →</span>
              <PhotoUploader endpoint={`/parts/${part.id}/photos`} cameraFirst onFiles={(files) => files.forEach(addShot)}
                buttonLabel="Native camera" testidPrefix="rapid-photo" />
            </div>
          )}

          {/* shots — instant preview + per-photo status; delete any (own, this session) */}
          {shots.length > 0 && (
            <div>
              <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1.5">Photos ({shots.length})</div>
              <div data-testid="rapid-shots-strip" className="flex flex-wrap gap-2">
                {shots.map((s, i) => (
                  <div key={s.id} data-testid={`rapid-shot-${i}`} className={`relative w-24 h-24 rounded-lg overflow-hidden border-2 ${s.status === "error" ? "border-[#B01E28]" : s.status === "done" || s.status === "skipped" ? "border-emerald-300" : "border-[#C76714]/50"} bg-slate-50`}>
                    <img src={s.url} alt={`shot ${i + 1}`} className="w-full h-full object-cover" />
                    {/* status overlay */}
                    {s.status === "uploading" && <span className="absolute inset-0 bg-black/40 flex items-center justify-center"><Loader2 className="w-6 h-6 text-white animate-spin" /></span>}
                    {(s.status === "done" || s.status === "skipped") && (
                      <span className="absolute bottom-0.5 right-0.5 w-5 h-5 rounded-full bg-emerald-600 text-white flex items-center justify-center">
                        {s.status === "skipped" ? <Copy className="w-3 h-3" title="already there" /> : <Check className="w-3 h-3" />}
                      </span>
                    )}
                    {s.status === "error" && (
                      <button type="button" data-testid={`rapid-shot-retry-${i}`} onClick={() => retryShot(s)}
                        className="absolute inset-0 bg-[#B01E28]/60 flex flex-col items-center justify-center text-white text-[10px] font-semibold">
                        <RotateCw className="w-5 h-5 mb-0.5" />Retry
                      </button>
                    )}
                    <button type="button" data-testid={`rapid-shot-del-${i}`} onClick={() => deleteShot(s)}
                      className="absolute top-0.5 right-0.5 w-5 h-5 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-[#B01E28]" title="Remove this shot">
                      <X className="w-3 h-3" />
                    </button>
                    {i === shots.length - 1 && (s.status === "done" || s.status === "skipped") && savedCount === 1 && (
                      <span className="absolute top-0.5 left-0.5 rounded bg-[#B01E28] text-white text-[8px] font-bold px-1">TAG</span>
                    )}
                    {s.file_id && (s.status === "done" || s.status === "skipped") && (
                      <div className="absolute bottom-0.5 left-0.5">
                        <PhotoFixAction scope="part" recordId={part.id} photo={{ file_id: s.file_id }} compact
                          testid={`rapid-shot-fix-${i}`} label="Wrong part?"
                          onDone={() => setShots((prev) => prev.filter((x) => x.id !== s.id))} />
                      </div>
                    )}
                  </div>
                ))}
              </div>
              {failedCount > 0 && <p data-testid="rapid-failed-hint" className="text-xs text-[#B01E28] flex items-center gap-1 mt-1.5"><AlertTriangle className="w-3.5 h-3.5" />{failedCount} photo{failedCount === 1 ? "" : "s"} failed — tap a red tile to retry.</p>}
            </div>
          )}

          {/* actions */}
          <div className="flex flex-wrap gap-2 pt-1">
            <Button data-testid="rapid-next-part" onClick={() => requestLeave({ type: "next" })} disabled={!savedCount && !hasPending}
              className="rounded-xl h-12 px-5 bg-[#B01E28] hover:bg-[#8f1820] text-white text-base font-semibold">
              <SkipForward className="w-5 h-5 mr-2" />Next Part
            </Button>
            <Button data-testid="rapid-complete" onClick={() => requestLeave({ type: "complete" })} variant="outline"
              className="rounded-xl h-12 px-5 border-emerald-500 text-emerald-700 hover:bg-emerald-50 text-base font-semibold">
              <CheckCircle2 className="w-5 h-5 mr-2" />Complete
            </Button>
          </div>
        </div>
      )}

      {/* ---------------- DONE STAGE ---------------- */}
      {stage === "done" && (
        <div data-testid="rapid-done-stage" className="rounded-2xl border border-emerald-200 bg-emerald-50 p-8 text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-emerald-500 text-white mb-4">
            <CheckCircle2 className="w-12 h-12" />
          </div>
          <div data-testid="rapid-done-summary" className="font-display text-2xl font-bold text-slate-900">
            {session.photos} photo{session.photos === 1 ? "" : "s"} · {session.parts} part{session.parts === 1 ? "" : "s"} ✓
          </div>
          <p className="text-sm text-slate-600 mt-1">All photos are attached to their scanned tags.{(user?.name || user?.email) ? ` Signed off by ${user?.name || user?.email}.` : ""}</p>
          <div className="flex justify-center gap-2 mt-6">
            <Button data-testid="rapid-done-home" onClick={() => nav("/home")} className="rounded-xl h-12 px-6 bg-slate-900 hover:bg-slate-700 text-white text-base font-semibold">
              <Home className="w-5 h-5 mr-2" />Return home
            </Button>
            <Button data-testid="rapid-done-again" onClick={() => { setStage("scan"); setSession({ parts: 0, photos: 0 }); setPart(null); setShots([]); setArmNextScan(false); }}
              variant="outline" className="rounded-xl h-12 px-6 border-slate-300 text-base font-semibold">
              <ScanLine className="w-5 h-5 mr-2" />Start a new session
            </Button>
          </div>
        </div>
      )}

      {/* ---------------- NAV GUARD DIALOG ---------------- */}
      {guard && (
        <div data-testid="rapid-guard" className="fixed inset-x-0 top-vv h-vv z-50 flex items-center justify-center bg-black/50 p-4 overflow-y-auto" onClick={() => setGuard(null)}>
          <div className="bg-white rounded-2xl max-w-sm w-full p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 text-[#B01E28] font-bold text-lg mb-2">
              <AlertTriangle className="w-5 h-5" />{failedCount > 0 ? "Some photos failed to upload" : "Photos still uploading"}
            </div>
            {failedCount > 0 ? (
              <>
                <p className="text-sm text-slate-600"><span className="font-bold">{failedCount}</span> photo{failedCount === 1 ? "" : "s"} didn't upload{uploadingCount ? `, and ${uploadingCount} still uploading` : ""}. Retry them, or discard the failed ones and continue?</p>
                <div className="flex flex-col gap-2 mt-4">
                  <Button data-testid="rapid-guard-retry" onClick={() => shotsRef.current.filter((s) => s.status === "error").forEach(retryShot)}
                    className="rounded-xl h-11 bg-[#C76714] hover:bg-[#a8560f] text-white font-semibold"><RotateCw className="w-4 h-4 mr-2" />Retry {failedCount} failed</Button>
                  <Button data-testid="rapid-guard-discard" onClick={guardDiscardFailed} variant="outline" className="rounded-xl h-11 font-semibold text-[#B01E28] border-[#B01E28]/40">
                    Discard {failedCount} failed &amp; continue
                  </Button>
                  <Button data-testid="rapid-guard-cancel" onClick={() => setGuard(null)} variant="ghost" className="rounded-xl h-11 font-semibold">Keep shooting</Button>
                </div>
              </>
            ) : (
              <>
                <p className="text-sm text-slate-600 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /><span className="font-bold">{uploadingCount}</span> photo{uploadingCount === 1 ? "" : "s"} finishing upload — hang on, you'll continue automatically.</p>
                <div className="flex flex-col gap-2 mt-4">
                  <Button data-testid="rapid-guard-cancel" onClick={() => setGuard(null)} variant="outline" className="rounded-xl h-11 font-semibold">Keep shooting</Button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
